import React, { useState, useMemo } from "react";
import {
    Stepper,
    Step,
    StepLabel,
    TextField,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Button,
    Pagination,
    Box,
    Checkbox,
    FormControlLabel,
} from "@mui/material/";
import styles from "./styles";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { format, helpers } from "../../../common/lib";
import useDataUnchecked from "../../../AuditAssets/Assets/useDataUnchecked";
import useAssetJourneyHelpers from "../useAssetJourneyHelpers";
import ExcludeAsset from ".";
import ImageStepper from "../Stepper";
import actions from "../../../Store/actions";

const ExcludeSearch = ({ actionBtnUpdate }) => {
    const { t } = useTranslation();
    const steps = [t("selectAssets"), t("selectExclusion"), t("reviewAssets")];
    const { audits, typesList, assetDetails } = useSelector((state) => state);
    const { makes, assetTypes, exclusionReasonTypes } = typesList;
    const dispatch = useDispatch();
    const { exclusionReasonDate, exclusionReasonTypeId, notes } = assetDetails;
    const { disabledExclude } = useAssetJourneyHelpers(
        actionBtnUpdate,
        "exclude"
    );

    const [uncheckedCount, dataUnchecked] = useDataUnchecked(audits.item.id);
    const cardsData = dataUnchecked;

    const itemsPerPage = 10;

    const [excludeStep, setActiveExcludeStep] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCards, setSelectedCards] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [excludeAll, setExcludeAll] = useState(false);

    const filteredCards = cardsData.filter((card) =>
        card.assetCore.identifier1.toLowerCase().includes(searchTerm)
    );

    const disableNext = useMemo(() => {
        if (excludeStep === 0 && selectedCards.length === 0) return true;
        if (excludeStep === 1 && disabledExclude) return true;
    }, [disabledExclude, excludeStep, selectedCards]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value.toLowerCase());
        setCurrentPage(1);
    };

    const toggleCardSelection = (card) => {
        if (selectedCards.some((selected) => selected.id === card.id)) {
            const cards = selectedCards.filter(
                (selected) => selected.id !== card.id
            );
            setSelectedCards(cards);
            dispatch(
                actions.assetDetails.create.set({
                    ...assetDetails,
                    excludeAssets: cards,
                })
            );
        } else {
            const cards = [...selectedCards, card];
            setSelectedCards(cards);
            dispatch(
                actions.assetDetails.create.set({
                    ...assetDetails,
                    excludeAssets: cards,
                })
            );
        }
    };

    const handleChipDelete = (cardId) => {
        const cards = selectedCards.filter((card) => card.id !== cardId);
        setSelectedCards(cards);
        dispatch(
            actions.assetDetails.create.set({
                ...assetDetails,
                excludeAssets: cards,
            })
        );
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const currentCards = filteredCards.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    /** Handle back and next steps */
    const handleNext = () => {
        setActiveExcludeStep((prevStep) => prevStep + 1);
        dispatch(
            actions.assetDetails.create.set({
                ...assetDetails,
                excludeAssetsStep: excludeStep + 1,
            })
        );
    };
    const handleBack = () => {
        setActiveExcludeStep((prevStep) => prevStep - 1);
        dispatch(
            actions.assetDetails.create.set({
                ...assetDetails,
                excludeAssetsStep: excludeStep - 1,
            })
        );
    };

    const onSelectAll = (event) => {
        // const  = selectedCards.some((selected) => selected.id === card.id)

        setExcludeAll(event.target.checked);
        if (event.target.checked) setSelectedCards(filteredCards);
        else setSelectedCards([]);
        dispatch(
            actions.assetDetails.create.set({
                ...assetDetails,
                excludeAssets: filteredCards,
            })
        );
    };

    return (
        <Box sx={styles.container}>
            <Stepper
                activeStep={excludeStep}
                alternativeLabel
                sx={styles.stepper}
            >
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            {/* Main Content */}
            {excludeStep === 0 && (
                <Box sx={styles.stepsContainer}>
                    <TextField
                        label={t("search")}
                        variant="outlined"
                        fullWidth
                        value={searchTerm}
                        onChange={handleSearchChange}
                        sx={styles.searchField}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={excludeAll}
                                onChange={onSelectAll}
                            />
                        }
                        label={t("excludeAll")}
                    />
                    <Typography variant="body2" sx={styles.emptyMessage}>
                        {t("messages.selectedAssets", {
                            count: selectedCards.length,
                        })}
                    </Typography>

                    {/* Scrollable Assets Container */}

                    <Box sx={styles.assetsContainer}>
                        <Grid container spacing={2}>
                            {currentCards.map((card) => {
                                const isSelected = selectedCards.some(
                                    (selected) => selected.id === card.id
                                );
                                return (
                                    <Grid item xs={12} sm={6} key={card.id}>
                                        <Card
                                            onClick={() =>
                                                toggleCardSelection(card)
                                            }
                                            sx={styles.card(isSelected)}
                                        >
                                            {/* Checkbox in top-left corner */}
                                            <Checkbox
                                                size="small"
                                                checked={isSelected}
                                                onChange={() =>
                                                    toggleCardSelection(card)
                                                }
                                                sx={styles.checkbox}
                                            />
                                            <CardContent>
                                                <Typography
                                                    variant="caption"
                                                    component="p"
                                                >
                                                    {card.assetCore.identifier1}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    component="p"
                                                >
                                                    {card.assetCore.identifier2}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    component="p"
                                                >
                                                    {
                                                        helpers.list.getItemForId(
                                                            makes,
                                                            card.assetCore.make
                                                        ).name
                                                    }
                                                </Typography>

                                                <Typography
                                                    component="p"
                                                    variant="caption"
                                                >
                                                    {
                                                        helpers.list.getItemForId(
                                                            assetTypes,
                                                            card.assetCore.type
                                                        ).name
                                                    }
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                    {filteredCards.length > itemsPerPage && (
                        <Pagination
                            count={Math.ceil(
                                filteredCards.length / itemsPerPage
                            )}
                            page={currentPage}
                            onChange={handlePageChange}
                            sx={styles.pagination}
                        />
                    )}
                </Box>
            )}
            {excludeStep === 1 && (
                <Box sx={styles.stepsContainer}>
                    <ExcludeAsset />
                    <ImageStepper />
                </Box>
            )}
            {excludeStep === 2 && (
                <Box sx={styles.stepsContainer}>
                    <Typography variant="h6">Review Selected Assets</Typography>
                    {selectedCards.length === 0 ? (
                        <Typography variant="body2" sx={styles.emptyMessage}>
                            No assets selected.
                        </Typography>
                    ) : (
                        <Box sx={styles.chipContainer}>
                            {selectedCards.map((card) => (
                                <Chip
                                    key={card.id}
                                    label={card.assetCore.identifier1}
                                    onDelete={() => handleChipDelete(card.id)}
                                    style={{ margin: "5px" }}
                                />
                            ))}
                        </Box>
                    )}
                    <Typography variant="body2" sx={styles.emptyMessage}>
                        {t("exclusionReason")}
                    </Typography>{" "}
                    <Typography variant="caption" sx={styles.emptyMessage}>
                        {
                            helpers.list.getItemForId(
                                exclusionReasonTypes,
                                exclusionReasonTypeId
                            ).name
                        }
                        {}
                    </Typography>{" "}
                    <Typography variant="body2" sx={styles.emptyMessage}>
                        {t("notes")}
                    </Typography>
                    <Typography variant="caption" sx={styles.emptyMessage}>
                        {notes.content}
                    </Typography>
                    {exclusionReasonDate && (
                        <>
                            <Typography
                                variant="body2"
                                sx={styles.emptyMessage}
                            >
                                {t("date")}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={styles.emptyMessage}
                            >
                                {format.dateTime(exclusionReasonDate)}
                            </Typography>
                        </>
                    )}
                </Box>
            )}
            {/* Navigation Buttons */}
            <Box sx={styles.navButtons}>
                <Button
                    variant="outlined"
                    disabled={excludeStep === 0}
                    onClick={handleBack}
                >
                    {t("back")}
                </Button>
                {excludeStep < steps.length - 1 ? (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        disabled={disableNext}
                    >
                        {t("next")}
                    </Button>
                ) : null}
            </Box>
        </Box>
    );
};

export default ExcludeSearch;
